import actionTypes from '../actionTypes';
import api from 'src/utils/api';
import { scrollToPostId } from 'src/utils/scroll';
import { setAlert } from 'src/modules/shared/alert/actions';
import { closeModal } from 'src/modules/shared/modal/actions';
import { enforcePostStructure } from 'src/utils/dataStructure/enforce';
import { addToDismissList, getDismissedFeaturedPostIds } from 'src/utils/featuredPosts';
import config from 'config/config';
import Router from 'next/router';
import * as log from 'loglevel';

export const lockPost = (postId, communitySlug) => updatePostLock(postId, communitySlug, true);
export const unlockPost = (postId, communitySlug) => updatePostLock(postId, communitySlug, false);

export const updatePostLock = (postId, communitySlug, isLocked) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_POST_LOCK });
    try {
      const response = await api.auroraPut(`post/${postId}/lock`, {
        isLocked,
        groupCode: communitySlug,
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UPDATE_POST_LOCK_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UPDATE_POST_LOCK_SUCCESS,
        isLocked,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_LOCK_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const togglePostPin = (postId, communitySlug) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.UPDATE_POST_PIN });
    try {
      const response = await api.auroraPost(`post/${postId}/pin`, {
        groupCode: communitySlug,
      });
      const { data: { payload: { isPinned = false } = {}, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UPDATE_POST_PIN_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UPDATE_POST_PIN_SUCCESS,
        isPinned,
      });
      dispatch(setAlert(status.message, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_PIN_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const deletePost = (postId, communitySlug, superParentId, successMessage) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.DELETE_POST });
    try {
      const response = await api.auroraDel(`post/${postId}`, {
        params: {
          groupCode: communitySlug,
          id: postId,
          superParentId,
          parentType: 'posts',
        },
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.DELETE_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.DELETE_POST_SUCCESS,
        postId,
        superParentId,
      });
      if (!superParentId || superParentId === postId) {
        dispatch(setAlert(successMessage, 'success'));
        Router.push(
          `/community/communityHome?slug=${communitySlug}`,
          `/${communitySlug}`,
        );
      }
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.DELETE_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const fetchCurrentCommunityRoles = (communityId, slug, showRestrictiveRoles) => {
  return async (dispatch) => {
    dispatch({ type:actionTypes.FETCH_CURRENT_COMMUNITY_ROLES });
    try {
      const response = await api.auroraGet('roles', {
        params: {
          groupId: communityId,
          groupCode: slug,
          showRestrictiveRoles },
      });
      const { data: { payload: { roles } = {}, status } } = response;

      if (status && !status.success) {
        return dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_FAILURE });
      }

      dispatch({
        type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_SUCCESS,
        roles,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.FETCH_CURRENT_COMMUNITY_ROLES_FAILURE });
    }
  };
};

export const assignBadges = (roleId, groupCode, userId) => {
  return async (dispatch) => {
    dispatch({ type:actionTypes.ASSIGN_BADGES });
    try {
      const response = await api.auroraPost('user-roles', {
        groupCode,
        userId,
        roleId,
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.ASSIGN_BADGES_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        return;
      }

      dispatch({
        type: actionTypes.ASSIGN_BADGES_SUCCESS,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.ASSIGN_BADGES_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const removeBadges = (roleId, groupCode, userId) => {
  return async (dispatch) => {
    dispatch({ type:actionTypes.REMOVE_BADGES });
    try {
      const response = await api.auroraDel('user-roles', {
        params: {
          roleId,
          groupCode,
          userId,
        },
      });
      const { data: { status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.REMOVE_BADGES_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        return;
      }

      dispatch({
        type: actionTypes.REMOVE_BADGES_SUCCESS,
      });
    } catch (ex) {
      dispatch({ type: actionTypes.REMOVE_BADGES_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const restrictUser = (user, communitySlug, successMessage, alreadyRestrictedMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.RESTRICT_USER });
    try {
      const response = await api.auroraPost('user-roles', {
        groupCode: communitySlug,
        userId: user.id,
        roleId: config.roles.restricted,
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: status });
        if (status.responseCode === '401002') {
          dispatch(setAlert(status.message));
          return;
        }
        dispatch(setAlert(alreadyRestrictedMessage));
        return;
      }

      dispatch({
        type: actionTypes.RESTRICT_USER_SUCCESS,
      });
      dispatch(setAlert(successMessage, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const reportPost = ({ reason, comment, postId, communitySlug, successMessage }) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.RESTRICT_USER });
    try {
      const response = await api.auroraPost(`post/${postId}/report`, {
        reason,
        comment,
        id: postId,
        groupCode: communitySlug,
        parentType: 'posts',
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.RESTRICT_USER_SUCCESS,
      });
      dispatch(setAlert(successMessage, 'success'));
      dispatch(closeModal());
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.RESTRICT_USER_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const likePost =
  (postId, superParentId, communitySlug) => updatePostLike(postId, superParentId, communitySlug, true);
export const unlikePost =
  (postId, superParentId, communitySlug) => updatePostLike(postId, superParentId, communitySlug, false);

export const updatePostLike = (postId, parentPostId, communitySlug, isLiked) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.LIKE_POST });
    try {
      const response = await api.auroraPost(`post/${postId}/rate`, {
        op: isLiked ? '+' : '-',
        superParentId: parentPostId,
        id: postId,
        groupCode: communitySlug,
        parentType: 'posts',
      });
      const { data: { payload: { hasRated }, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.LIKE_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.LIKE_POST_SUCCESS,
        isLiked: hasRated,
        postId,
        parentPostId,
        communitySlug,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.LIKE_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const followPost = (postId, trackLabel, successMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.FOLLOW_POST });
    try {
      const response = await api.auroraPost('follow/post', {
        Ids: [postId],
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.FOLLOW_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.FOLLOW_POST_SUCCESS,
        postId,
        trackLabel,
      });
      dispatch(setAlert(successMessage, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.FOLLOW_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const unfollowPost = (postId, successMessage) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionTypes.UNFOLLOW_POST });
    try {
      const response = await api.auroraDel('follow/post', {
        params: {
          Ids: [postId],
        },
      });
      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UNFOLLOW_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      dispatch({
        type: actionTypes.UNFOLLOW_POST_SUCCESS,
        postId,
      });
      dispatch(setAlert(successMessage, 'success'));
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UNFOLLOW_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

export const respondPost = ({postId, superParentId, body, communitySlug, mentionedUsers = []}) => {
  return async (dispatch, getState) => {
    const { context: { currentUser } } = getState();
    dispatch({ type: actionTypes.REPLY_POST });
    try {
      const response = await api.auroraPost(`post/${postId}/respond`, {
        parentId: postId,
        superParentId,
        body,
        groupCode: communitySlug,
        username: currentUser.username,
        mentionedUserIds: mentionedUsers.map(x => x.userId),
        parentType: 'posts',
      });

      const { data: { payload, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.REPLY_POST_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return false;
      }

      const { post: postData } = payload;
      const post = enforcePostStructure(postData, [...mentionedUsers, hideNonSpecialRolesForUser(currentUser)]);

      await dispatch({
        type: actionTypes.REPLY_POST_SUCCESS,
        post,
        communitySlug,
      });
      scrollToPostId(post.id);

      return { postId: post.id };
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.REPLY_POST_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};


export const editPostReply = ({postId, superParentId, body, communitySlug, successMessage, mentionedUsers = []}) => {
  return async (dispatch, getState) => {
    const { context: { currentUser } } = getState();
    dispatch({ type: actionTypes.UPDATE_POST_REPLY });
    try {
      const response = await api.auroraPut('post', {
        id: postId,
        superParentId,
        body,
        groupCode: communitySlug,
        username: currentUser.username,
        mentionedUserIds: mentionedUsers.map(x => x.userId),
        parentType: 'posts',
      });

      const { data: { payload: { post: postData } = {}, status } } = response;

      if (!!status && !status.success) {
        dispatch({ type: actionTypes.UPDATE_POST_REPLY_FAILURE, error: status });
        dispatch(setAlert(status.message));
        return;
      }

      const post = enforcePostStructure(postData, [...mentionedUsers, hideNonSpecialRolesForUser(currentUser)]);

      dispatch(setAlert(successMessage, 'success'));
      return dispatch({
        type: actionTypes.UPDATE_POST_REPLY_SUCCESS,
        post,
      });
    } catch (ex) {
      log.error(ex);
      dispatch({ type: actionTypes.UPDATE_POST_REPLY_FAILURE, error: ex });
      dispatch(setAlert(ex.message));
    }
  };
};

const hideNonSpecialRolesForUser = user => {
  const nonSpecialRoles = ['HealthUnlockedMember', 'CommunityMember'];
  return {
    ...user,
    roles: !user.roles ? [] : user.roles.filter(r => nonSpecialRoles.indexOf(r) == -1),
  };
};

export const dismissFeaturedPost = (featuredPostId) => {
  addToDismissList(featuredPostId);
  return {
    type: actionTypes.DISMISS_POST_FEATURED_POST_SUCCESS,
    dismissedFeaturedPostIds: getDismissedFeaturedPostIds(),
  };
};

export const setPendingVisitorReply = (visitorReplyData) => {
  return { type: actionTypes.SET_VISITOR_REPLY_DATA, visitorReplyData };
};



// WEBPACK FOOTER //
// ./src/modules/community/singlePost/actions/actions.js